import { AttributeDefinitionType, TaskMode } from 'Types/Tasks.types'
import { useMemo, useState } from 'react'
import styles from './JSONAnnotationTask.module.less'
import { JSONObject } from 'Types/Common.types'
import Flex from 'antd/lib/flex'
import { DynamicCollapseView } from './DynamicCollapseView'
import { useTaskContext } from 'Contexts/TaskContext'
import { Allotment } from 'allotment'
import classes from 'Layouts/Workbench/WorkbenchLayout.module.less'
import Progress from 'antd/lib/progress'
import { JSONAnnotationContentDetail } from './JSONAnnotationContentDetail'
import { TargetedAttributeGroup } from 'Core/AttributeCore/AttributeGroup/TargetedAttributeGroup'
import { DEFAULT_SELECTED_PATH } from 'Constants/attributes.constants'
import { ErrorIcon } from 'Components/common/Icons/ErrorIcon'
import { CloseOutlined } from '@ant-design/icons'

type Props = {
    content: JSONObject
    isReadOnly: () => boolean
    mode: TaskMode
    getShowFillMandatoryFieldsError: () => boolean
    setShowFillMandatoryFieldsError: (show: boolean) => void
}

export const JSONAnnotationContent = (props: Props) => {
    const taskContext = useTaskContext()
    const showFillMandatoryFieldsError = props.getShowFillMandatoryFieldsError()
    const metadata =
        taskContext.studio.taskConfig.assignment.task.json_schema_metadata?.metadata ?? null

    const attributeManager = taskContext.getAttributeManager?.()
    const attributeGroups = attributeManager?.getAttributeGroupsByDefinitionType(
        AttributeDefinitionType.TARGETED_ATTRIBUTES
    )
    const hasPathLevelAttributes = !!attributeGroups?.length

    const [selectedPath, setSelectedPath] = useState<string>(DEFAULT_SELECTED_PATH)

    const errorBanner = useMemo(() => {
        if (!showFillMandatoryFieldsError) return null
        return (
            <Flex className={styles['error-message-container']} justify="space-between">
                <Flex gap={4}>
                    <ErrorIcon />
                    <div className={styles['error-message-text']}>
                        Please fill the mandatory fields before submission
                    </div>
                </Flex>
                <CloseOutlined onClick={() => props.setShowFillMandatoryFieldsError(false)} />
            </Flex>
        )
    }, [props, showFillMandatoryFieldsError])

    const progressData = (() => {
        if (!hasPathLevelAttributes) {
            return null
        }

        let totalMandatoryAttributes = 0
        let filledMandatoryAttributes = 0

        attributeGroups.forEach(group => {
            const mandatoryVisibleAttributes = (
                group as TargetedAttributeGroup
            ).getVisibleMandatoryAttributes(
                group.getElement()?.element?.props.props.getValues() || {}
            )
            totalMandatoryAttributes += mandatoryVisibleAttributes.length
            const groupElement = group.getElement()
            const allValues = groupElement?.element?.props.props.getValues() || {}

            mandatoryVisibleAttributes.forEach(attr => {
                const value = allValues[attr.name]
                if (value !== undefined) {
                    filledMandatoryAttributes++
                }
            })
        })

        const progressPercent =
            totalMandatoryAttributes > 0
                ? Math.round((filledMandatoryAttributes / totalMandatoryAttributes) * 100)
                : 100

        return {
            totalMandatoryAttributes,
            filledMandatoryAttributes,
            progressPercent,
        }
    })()

    const getStatusForPath = useMemo(() => {
        if (!attributeManager) {
            return () => ({ isComplete: false })
        }

        const attributeGroups = attributeManager.getAttributeGroupsByDefinitionType(
            AttributeDefinitionType.TARGETED_ATTRIBUTES
        )

        function getStatus(path: string) {
            const groupsInPath = attributeGroups.filter(group => group.groupKey?.includes(path))
            const isAnyGroupNotFilled = groupsInPath.some(
                group => !group.areMandatoryAttributesFilled
            )
            if (isAnyGroupNotFilled) {
                return { isComplete: false }
            }
            return { isComplete: true }
        }

        return getStatus
    }, [attributeManager])

    return (
        <div className={styles['container']}>
            <div data-testid="allotment-container">
                <Allotment vertical={false} className={classes.allotmentContent}>
                    <Allotment.Pane
                        minSize={400}
                        preferredSize={'50%'}
                        visible={true}
                        className={styles['left-pane']}
                    >
                        <Flex vertical gap={25} data-testid="left-pane">
                            {errorBanner}
                            {hasPathLevelAttributes && (
                                <Flex gap={12} vertical>
                                    <div className={styles['collapse-view-heading']}>
                                        Select each node & fill attributes
                                    </div>
                                    <Flex vertical>
                                        <div className={styles['progress-info']}>
                                            {progressData?.filledMandatoryAttributes} of{' '}
                                            {progressData?.totalMandatoryAttributes} mandatory
                                            fields completed
                                        </div>
                                        <Progress
                                            className={styles['progress']}
                                            size={'small'}
                                            percent={progressData?.progressPercent}
                                            showInfo={false}
                                            strokeColor={'#7C53A5'}
                                            trailColor={'#E3E6E8'}
                                        />
                                    </Flex>
                                </Flex>
                            )}
                            <div
                                className={`${styles['collapse-view-container']} ${
                                    errorBanner
                                        ? styles['height-with-error']
                                        : styles['height-without-error']
                                }`}
                                data-testid="dynamic-collapse-view"
                            >
                                <DynamicCollapseView
                                    mode={props.mode}
                                    path={'$'}
                                    title={''}
                                    data={props.content}
                                    level={0}
                                    isReadOnly={props.isReadOnly}
                                    getStatusForPath={getStatusForPath}
                                    onSelectPath={setSelectedPath}
                                    selectedPath={selectedPath}
                                    metadata={metadata}
                                />
                            </div>
                        </Flex>
                    </Allotment.Pane>
                    <Allotment.Pane
                        minSize={400}
                        preferredSize={'50%'}
                        visible={true}
                        className={styles['right-pane']}
                    >
                        <div className={styles['collapse-view-container']} data-testid="right-pane">
                            <JSONAnnotationContentDetail
                                selectedPath={selectedPath}
                                setSelectedPath={setSelectedPath}
                                content={props.content}
                            />
                        </div>
                    </Allotment.Pane>
                </Allotment>
            </div>
        </div>
    )
}
